import { useMutation, useQuery, useQueryClient } from '@/hooks/useQuery'
import { queryKeys } from '@/api/queryKeys'
import * as basicInformationApi from '@/api/basic-information'
import type {
  MedicalDeviceBrandFilter,
  UpsertMedicalDeviceBrandRequest,
  UpsertMedicalDeviceClassificationCatalogRequest,
  UpsertSupplierRequest,
} from '@/api/types/basic-information'

export function useMedicalDeviceBrands(filter: MedicalDeviceBrandFilter = {}) {
  return useQuery({
    queryKey: queryKeys.medicalDeviceBrands(filter),
    queryFn: () => basicInformationApi.getMedicalDeviceBrands(filter),
  })
}

export function useCreateMedicalDeviceBrand() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertMedicalDeviceBrandRequest) => basicInformationApi.createMedicalDeviceBrand(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'medical-device-brands'] }),
  })
}

export function useUpdateMedicalDeviceBrand() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertMedicalDeviceBrandRequest }) =>
      basicInformationApi.updateMedicalDeviceBrand(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'medical-device-brands'] }),
  })
}

export function useDeleteMedicalDeviceBrand() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: basicInformationApi.deleteMedicalDeviceBrand,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'medical-device-brands'] }),
  })
}

export function useMedicalDeviceCatalogTree() {
  return useQuery({ queryKey: queryKeys.medicalDeviceCatalogs, queryFn: basicInformationApi.getMedicalDeviceCatalogTree })
}

export function useCreateMedicalDeviceCatalog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertMedicalDeviceClassificationCatalogRequest) => basicInformationApi.createMedicalDeviceCatalog(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.medicalDeviceCatalogs }),
  })
}

export function useUpdateMedicalDeviceCatalog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertMedicalDeviceClassificationCatalogRequest }) =>
      basicInformationApi.updateMedicalDeviceCatalog(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.medicalDeviceCatalogs }),
  })
}

export function useDeleteMedicalDeviceCatalog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: basicInformationApi.deleteMedicalDeviceCatalog,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.medicalDeviceCatalogs }),
  })
}

export function useSuppliers(keyword?: string) {
  return useQuery({
    queryKey: queryKeys.suppliers({ keyword }),
    queryFn: () => basicInformationApi.getSuppliers(keyword),
  })
}

export function useCreateSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertSupplierRequest) => basicInformationApi.createSupplier(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'suppliers'] }),
  })
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpsertSupplierRequest }) => basicInformationApi.updateSupplier(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'suppliers'] }),
  })
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: basicInformationApi.deleteSupplier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['basic-information', 'suppliers'] }),
  })
}
